import React from 'react'
import PointsSvg from 'images/Points.svg'
import { Divider } from '@mui/material'
import { Link } from 'react-router-dom'
import {
  ACCOUNT_LOGOUT_URL,
  CONTACT_US_FLATPAGE_URL,
  CREDITS_URL,
  GIFT_CARD_URL,
  PROFILE_ADDRESSES_URL,
  PROFILE_CHANGE_INFO_URL,
  PROFILE_DISCOUNT_URL,
  PROFILE_FAVORITES_URL,
  PROFILE_ORDER_HISTORY_URL,
  PROFILE_PATTERN_PACKS_URL,
  PROFILE_REWARDS_URL,
  PROFILE_SUBSCRIPTIONS_URL,
} from '../../../urls'
import { DEFAULT_CURRENCY } from '../../../../constants'
import CreditsSvg from 'images/Credits.svg'
import { showPrice } from '../../../misc/price'
import { useTranslation } from 'react-i18next'
import { MeQuery } from '@graphql/queries/__generated__/me.generated'
import WholesaleMode from '@components/WholesaleMode'

interface Props {
  user: MeQuery['me'] | null

  handleClickContactUs(): void
}

const HeaderProfilePanel: React.FC<Props> = ({ user, handleClickContactUs }) => {
  const { t } = useTranslation()

  return (
    <>
      <WholesaleMode hide>
        <div className='flex items-center text-13'>
          <div className='w-40 mr-10'>
            <img src={PointsSvg} alt={t('points', 'Points')} className='max-w-full' />
          </div>
          <div className='flex-1'>
            <span className='text-gray-800'>
              {t('description.rewards', 'Earn one point for every dollar you spend.')}
            </span>
            <strong>{t('points', { defaultValue: '{{count}} points', count: 150 })} = </strong>
            <strong>$10 {t('reward', 'Reward')}.</strong>
          </div>
        </div>
        <div className='my-20'>
          <Divider />
        </div>
      </WholesaleMode>
      <ul className='m-0 p-0 list-none text-13'>
        {user && (
          <>
            <WholesaleMode hide>
              <li className='pb-8'>
                <Link to={PROFILE_REWARDS_URL} className='no-underline hover:underline block'>
                  {t('menu.rewards', {
                    defaultValue: 'My Rewards ({{totalBalance}} out of {{rpThreshold}})',
                    totalBalance: user.totalPointsBalanceAvailable,
                    rpThreshold: 150,
                  })}
                </Link>
              </li>
            </WholesaleMode>
            <li className='pb-8'>
              <Link to={PROFILE_DISCOUNT_URL} className='no-underline hover:underline block'>
                {t('menu.myDiscount', 'My Discount')}
              </Link>
            </li>
            <WholesaleMode hide>
              <li className='pb-8'>
                <Link to={CREDITS_URL} className='no-underline hover:underline block'>
                  {t('menu.credits', {
                    defaultValue: `My Credits ({{balance}})`,
                    balance: showPrice({
                      amount: +user?.totalBalance?.gross || 0,
                      currency: user?.totalBalance?.currency || 'USD',
                    }),
                  })}
                </Link>
              </li>
              <li className='pb-8'>
                <Link to={PROFILE_PATTERN_PACKS_URL} className='no-underline hover:underline block'>
                  {t('menu.patternPacks', 'Pattern Packs')}
                </Link>
              </li>
              <li className='pb-8'>
                <Link to={GIFT_CARD_URL} className='no-underline hover:underline block'>
                  {t('menu.giftCard', 'Gift Cards')}
                </Link>
              </li>
            </WholesaleMode>
            <li className='pb-8'>
              <Link to={PROFILE_ORDER_HISTORY_URL} className='no-underline hover:underline block'>
                {t('menu.orderHistory', 'Order History')}
              </Link>
            </li>
            <li className='pb-8'>
              <Link to={PROFILE_FAVORITES_URL} className='no-underline hover:underline block'>
                {t('menu.favorites', 'Favorites')}
              </Link>
            </li>
            <li className='pb-8'>
              <Link to={PROFILE_CHANGE_INFO_URL} className='no-underline hover:underline block'>
                {t('menu.accountDetails', 'Account Details')}
              </Link>
            </li>
            <li className='pb-8'>
              <Link to={PROFILE_ADDRESSES_URL} className='no-underline hover:underline block'>
                {t('menu.addressBook', 'Address Book')}
              </Link>
            </li>
            <li className='pb-8'>
              <Link to={PROFILE_SUBSCRIPTIONS_URL} className='no-underline hover:underline block'>
                {t('menu.contactPreferences', 'Contact Preferences')}
              </Link>
            </li>
          </>
        )}
        <li className='pb-8'>
          <Link to={ACCOUNT_LOGOUT_URL} className='no-underline hover:underline block'>
            {t('menu.signOut', 'Sign Out')}
          </Link>
        </li>
        <li className='pb-8'>
          <Link
            to={CONTACT_US_FLATPAGE_URL}
            onClick={e => {
              e.preventDefault()
              handleClickContactUs()
            }}
            className='no-underline hover:underline block'
          >
            {t('menu.contactUs', 'Contact Us')}
          </Link>
        </li>
      </ul>
      <WholesaleMode hide>
        <div className='my-20'>
          <Divider />
        </div>
        <div className='flex items-center text-13'>
          <div className='w-35 mr-10'>
            <img src={CreditsSvg} alt={t('credits', 'Credits')} className='max-w-full' />
          </div>
          <div className='flex-1'>
            <span className='text-gray-800'>
              {t('title.stockUpOnCredits', 'Stock up on Credits')}
              {'. '}
              {t('description.creditsSaveMoneyButBulk', 'Save money and get free bonus credits when you buy in bulk.')}
            </span>
            &nbsp;
            <Link to={CREDITS_URL} className='no-underline hover:underline text-black'>
              <strong>{t('button.getMoreCredits', 'Get more credits')}</strong>
            </Link>
          </div>
        </div>
      </WholesaleMode>
    </>
  )
}

export default HeaderProfilePanel
